import React from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { libraries as libs } from "config/googleMapLibs";
import { useSelector } from "react-redux";

const useGoogleApi = () => {
  const { selectedStore } = useSelector((state) => state.store);

  const [placeInfo, setPlaceInfo] = React.useState(null);
  const [reviewsInfo, setReviewsInfo] = React.useState(null);

  const isMounted = React.useRef(true);
  const placesDivRef = React.useRef(null);
  const placesServiceRef = React.useRef(null);

  const libraries = React.useMemo(() => libs, []);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-maps-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries, // MUST include "places"
    language: "en",
    region: "us",
    preventGoogleFontsLoading: true,
  });

  const ensurePlacesService = () => {
    if (loadError) throw new Error(`Google Maps load error: ${String(loadError)}`);
    if (!isLoaded) throw new Error("Google Maps script not loaded yet.");
    if (!window.google?.maps?.places)
      throw new Error('Places library missing. Ensure libraries includes "places".');

    // Create the hidden div once
    if (!placesDivRef.current) {
      const div = document.createElement("div");
      div.style.display = "none";
      document.body.appendChild(div);
      placesDivRef.current = div;
    }

    if (!placesServiceRef.current) {
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        placesDivRef.current
      );
    }

    return placesServiceRef.current;
  };

  const findPlaceFromText = async () => {
    const service = ensurePlacesService();

    const query = selectedStore?.baseLanguage?.name;
    if (!query) throw new Error("Store name missing.");

    const coords = selectedStore?.storeLocation?.coordinates;
    if (!coords || coords.length < 2) throw new Error("Store coordinates missing.");

    // GeoJSON usually: [lng, lat]
    const lng = coords[0];
    const lat = coords[1];

    const request = {
      query,
      fields: ["place_id", "name", "formatted_address", "geometry"],
      locationBias: new window.google.maps.LatLng(lat, lng),
    };

    return new Promise((resolve, reject) => {
      service.findPlaceFromQuery(request, (results, status) => {
        if (!isMounted.current) return;

        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !results?.length
        ) {
          reject(new Error(`findPlaceFromQuery failed: ${status}`));
          return;
        }

        const best = results[0];
        setPlaceInfo(best);
        resolve(best);
      });
    });
  };

  const getPlaceDetails = async (placeId) => {
    const service = ensurePlacesService();

    return new Promise((resolve, reject) => {
      service.getDetails(
        {
          placeId,
          fields: ["place_id", "name", "rating", "user_ratings_total", "reviews", "url"],
        },
        (result, status) => {
          if (!isMounted.current) return;

          if (
            status !== window.google.maps.places.PlacesServiceStatus.OK ||
            !result
          ) {
            reject(new Error(`getDetails failed: ${status}`));
            return;
          }

          setReviewsInfo({
            rating: result.rating,
            total: result.user_ratings_total,
            reviews: result.reviews || [],
            url: result.url,
            name: result.name,
          });

          resolve(result);
        }
      );
    });
  };

  React.useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;

      // Cleanup service + div
      placesServiceRef.current = null;

      if (placesDivRef.current) {
        placesDivRef.current.remove();
        placesDivRef.current = null;
      }

      setPlaceInfo(null);
      setReviewsInfo(null);
    };
  }, []);

  React.useEffect(() => {
    if (!isLoaded || !selectedStore) return;

    (async () => {
      try {
        setPlaceInfo(null);
        setReviewsInfo(null);
        await findPlaceFromText();
      } catch (e) {
        console.error("Google Places error:", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, selectedStore]);
  

  return {
    isLoaded,
    placeInfo,
    reviewsInfo,
    findPlaceFromText,
    getPlaceDetails,
  };
};

export default useGoogleApi;
